import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

const navLinks = [
  { label: "Home", href: "/" },
  { label: "About Us", href: "/about" },
  { label: "Careers", href: "/careers" },
  { label: "Portfolio", href: "/portfolio" },
  { label: "Investment Strategy", href: "/investment-strategy" },
  { label: "Insights", href: "/insights" },
  { label: "Contact Us", href: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="fixed top-5 left-0 right-0 z-50 max-w-7xl mx-auto px-6 md:px-12">
      <motion.nav
        data-testid="navbar"
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className={`w-full transition-all duration-500 ${
          mobileOpen ? "rounded-[24px]" : "rounded-full"
        } ${
          scrolled
            ? "bg-white/85 backdrop-blur-2xl shadow-lg shadow-slate-900/5 border border-slate-200/70"
            : "bg-white/70 backdrop-blur-2xl border border-slate-200/50"
        }`}
      >
        <div className="flex items-center justify-between px-6 py-2.5 md:px-8">
          {/* Logo */}
          <Link to="/" data-testid="navbar-logo" className="flex items-center flex-shrink-0">
            <span className="font-heading font-semibold text-[20px] tracking-tight whitespace-nowrap">
              <span className="text-[#08122a]">Nebula</span>{" "}
              <span style={{ color: '#5B6BCC' }}>Flow</span>
            </span>
          </Link>

          {/* Desktop Links — centered */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) =>
              link.href.startsWith("/") ? (
                <Link
                  key={link.label}
                  to={link.href}
                  data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className="px-3 py-1.5 text-[15px] font-medium text-slate-600 hover:text-blue-600 rounded-full hover:bg-blue-50/50 transition-all duration-200 whitespace-nowrap"
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.label}
                  href={link.href}
                  data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className="px-3 py-1.5 text-[15px] font-medium text-slate-600 hover:text-blue-600 rounded-full hover:bg-blue-50/50 transition-all duration-200 whitespace-nowrap"
                >
                  {link.label}
                </a>
              )
            )}
          </div>

          {/* CTA + Mobile Toggle */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <a
              href="#connect"
              data-testid="navbar-cta"
              className="hidden md:inline-flex items-center gap-1.5 px-4 py-2 text-[13px] font-medium text-white bg-[#0B1220] hover:bg-[#111827] rounded-full transition-all duration-200 group"
            >
              Get in touch
              <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <Link
              to="/contact"
              data-testid="navbar-cta-mobile"
              className="md:hidden inline-flex items-center gap-1 px-3 py-1.5 text-[12px] font-medium text-white bg-[#0B1220] hover:bg-[#111827] rounded-full transition-all duration-200 group"
            >
              Get in touch
              <ArrowUpRight className="w-3 h-3 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden p-2 rounded-full hover:bg-slate-100 transition-colors"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden overflow-hidden border-t border-slate-100"
            >
              <div className="px-5 py-4 flex flex-col gap-1">
                {navLinks.map((link) =>
                  link.href.startsWith("/") ? (
                    <Link
                      key={link.label}
                      to={link.href}
                      data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                      className="px-3 py-2.5 text-sm font-medium text-slate-600 hover:text-blue-600 hover:bg-blue-50/50 rounded-xl transition-all"
                      onClick={() => setMobileOpen(false)}
                    >
                      {link.label}
                    </Link>
                  ) : (
                    <a
                      key={link.label}
                      href={link.href}
                      data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                      className="px-3 py-2.5 text-sm font-medium text-slate-600 hover:text-blue-600 hover:bg-blue-50/50 rounded-xl transition-all"
                      onClick={() => setMobileOpen(false)}
                    >
                      {link.label}
                    </a>
                  )
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>
    </div>
  );
}
